import React, { useState, useRef } from 'react';
import { Tag, Input } from 'antd';
import { SearchOutlined, BookOutlined, MenuOutlined, CloseOutlined } from '@ant-design/icons';

// ─── Manual content ───────────────────────────────────────────────────────────
const SECTIONS = [
    {
        id: 'getting-started',
        icon: '🚀',
        title: 'Getting Started',
        color: '#1890ff',
        topics: [
            {
                id: 'gs-overview',
                title: 'Platform Overview',
                content: `
The Eswatini Quality of Care (QoC) Platform is a digital tool developed by the Ministry of Health's National Quality Management Programme (NQMP) to manage, track, and report on health facility quality assessments across the country.

**What the platform does:**
- Manages structured audits of health facilities against 10 QoC standard clauses
- Tracks compliance scores from audit to approval
- Provides analytics dashboards for national-level oversight
- Collects patient satisfaction surveys (inpatient and outpatient)
- Generates audit reports (PDF and Excel)
- Supports import of assessments from KoboCollect and Excel templates
- Syncs facility records from the national Health Information Exchange (HIE)

**Who uses the platform:**
- **Assessors** — conduct audits at facilities and submit responses
- **Approvers** — review and approve submitted assessments
- **Analysts / QoC Managers** — view dashboards and generate reports
- **Administrators** — manage users, roles, periods, standards setup, and system settings
                `
            },
            {
                id: 'gs-login',
                title: 'Logging In',
                content: `
Navigate to the platform URL provided by your system administrator. Enter your username and password. The platform uses role-based access — the menu items you see depend on which permissions your account has been granted.

**Permission modules:**
| Permission | Access |
|---|---|
| \`qoc_admin\` | Full admin — standards, periods, KPI targets, settings, all reports |
| \`qoc_assess\` | Create and submit assessments, import from KoboCollect/Excel |
| \`qoc_approve\` | Approve, reject, or request revision on submitted assessments |
| \`qoc_view\` | View dashboards, analytics, and compliance reports (read-only) |
| \`manage_roles_users\` | Create and manage users, roles, and module permissions |
| \`manage_facilities\` | Add, edit, and manage facility records |

If you cannot see a menu item you expect, contact your administrator to check your role permissions via **Settings → Access Control**.
                `
            },
            {
                id: 'gs-navigation',
                title: 'Navigating the Platform',
                content: `
The platform uses a left sidebar for navigation, organised into five sections:

**Administration**
- **Roles & Users** — Manage user accounts and role assignments
- **Facilities** — Manage the master facility list
- **User Manual** — This guide

**Quality of Care**
- **Standards Setup** — Manage clauses, MEs, and standards hierarchy
- **Approval Queue** — Review and approve submitted assessments
- **Assessments** — Create and manage facility audits
- **Import (KoBo)** — Import bulk assessments from KoboCollect or Excel
- **Corrective Actions** — CAP item management
- **Audit Team** — Team registry and assignments
- **Facility Profile** — Per-facility history and settings

**National Oversight**
- **Analytics** — Facility-level compliance overview
- **Compliance** — Detailed compliance tracking
- **KPI Targets** — Set performance targets by clause and period
- **Customer Surveys** — Inpatient/outpatient satisfaction surveys
- **Quality Index** — Composite QoC + survey weighted score

**Reports**
- **Executive Dashboard** — National-level performance (10 sections)
- **Facility Report** — Per-facility PDF/Excel audit reports
- **Custom Dashboards** — Build bespoke analytics views

**Settings** *(qoc_admin only)*
- **Assessment Periods** — Define audit cycles (e.g. FY2025/26)
- **Audit Schedules** — Plan and track facility audit schedules
- **Access Control** — Manage module permissions per role; create new modules
- **HIE Facility Sync** — Sync facilities from the national Health Information Exchange
                `
            }
        ]
    },
    {
        id: 'assessments',
        icon: '📋',
        title: 'Assessments',
        color: '#52c41a',
        topics: [
            {
                id: 'as-create',
                title: 'Creating an Assessment',
                content: `
1. Go to **Assessments** in the left navigation
2. Click **New Assessment**
3. Select the **Facility** and **Audit Period**
4. Select the **Clause** being assessed (e.g. AACC, COP, ISM)
5. Set the **Audit Date** and assign the **Lead Assessor**
6. Click **Save** to create a draft

Each assessment covers one clause. A full facility audit with all 10 clauses results in 10 separate assessment records, grouped together in reports and dashboards by period.

**Assessment statuses:**
| Status | Meaning |
|---|---|
| Draft | Created but not yet submitted |
| Submitted | Submitted for approval review — visible in dashboards |
| Approved | Approved — final status |
| Rejected | Sent back — requires correction before resubmission |
| Revision Requested | Approver has flagged specific items for correction |
                `
            },
            {
                id: 'as-responses',
                title: 'Recording Measurable Elements',
                content: `
Once an assessment is open, fill in responses for each **Measurable Element (ME)** — the specific questions within the clause.

**Fulfillment options:**
| Option | Score | When to use |
|---|---|---|
| Met | 100% | Requirement is fully satisfied |
| Partially Met | 50% | Requirement is partially satisfied |
| Not Met | 0% | Requirement is not satisfied |
| Not Applicable | Excluded | Does not apply to this facility type |
| Not Checked | Excluded | Could not be assessed during this audit |

**Scoring formula:**
- A = Total audited MEs (Met + Partially Met + Not Met only)
- B = 100 ÷ A (score per Met ME)
- Clause Score = (Met × B) + (Partially Met × B ÷ 2)

**Key Critical Standards (KCS):** MEs marked ⚠ KCS are critical. Failure on a KCS is flagged immediately.

For each ME you can also record:
- **Comment** — Required when Not Met or Partially Met
- **General Observation** — Descriptive note
- **Criticality** — High / Medium / Low
- **Area of Good Practice** — Positive findings
                `
            },
            {
                id: 'as-submit',
                title: 'Submitting for Approval',
                content: `
When all MEs are filled:
1. Review the clause score shown at the top of the assessment
2. Click **Submit for Approval**
3. The assessment moves to "Submitted" status and appears in the Approval queue

**Important:** Both "Submitted" and "Approved" assessments appear in dashboards and analytics. Once the approver acts, the status moves to "Approved," "Rejected," or "Revision Requested."

If rejected, you will receive the rejection reason and can edit and resubmit.
                `
            },
            {
                id: 'as-import',
                title: 'Importing from KoboCollect',
                content: `
If assessments were conducted offline using KoboCollect tablets:

1. Go to **Import (KoBo)** in the Quality of Care section
2. Select the **Clause** the export covers (critical — each KoboCollect export is for one clause)
3. Select the **Facility** and **Period**
4. Upload the exported KoboCollect file (.xlsx or .csv)
5. Review the mapped responses in the preview table
6. Click **Import** to save all records

**Common issue:** KoboCollect exports ME codes in short format (e.g. \`1.1\`) while the database stores prefixed codes (e.g. \`AACC.1.1\`). The system automatically applies the clause prefix during import — always select the correct clause before importing.

**Excel template import:**
1. Download the offline template from Assessment Import
2. Fill in fulfillment responses (dropdowns are provided)
3. Upload the completed file via Assessment Import
4. Review the mapped data and confirm
                `
            }
        ]
    },
    {
        id: 'approval',
        icon: '✅',
        title: 'Approval Workflow',
        color: '#fa8c16',
        topics: [
            {
                id: 'ap-review',
                title: 'Reviewing Submitted Assessments',
                content: `
Users with the \`qoc_approve\` permission can access the **Approval Queue**.

The list shows all submitted assessments with facility name, clause badge, audit date, score, star rating, and submission timestamp.

Click **Review** to open the full assessment with all ME responses.

**Actions available:**
| Action | Effect |
|---|---|
| Approve | Marks as approved; score finalised in analytics |
| Reject | Returns to assessor with rejection reason (required) |
| Request Revision | Flags specific MEs for correction with notes |

Filter the approval list by status, facility, clause, or period using the filter bar.
                `
            },
            {
                id: 'ap-bulk',
                title: 'Approving Multiple Assessments',
                content: `
Use the checkboxes on the approval list to select multiple assessments, then click **Bulk Approve** to approve all selected records at once.

This is useful when a facility has submitted all 10 clause assessments on the same date and all are ready for approval.
                `
            }
        ]
    },
    {
        id: 'dashboards',
        icon: '📊',
        title: 'Dashboards & Analytics',
        color: '#722ed1',
        topics: [
            {
                id: 'db-analytics',
                title: 'Analytics Dashboard',
                content: `
The **Analytics Dashboard** (National Oversight → Analytics) provides a facility-focused view:

- Select a facility from the dropdown to view its trend, clause scores, and compliance history
- Use the date range picker or period selector to filter
- The dashboard automatically loads the current active period's date range on first open

**Charts available:**
- Compliance trend over time
- Standard-wise score breakdown
- Facility comparison chart
                `
            },
            {
                id: 'db-executive',
                title: 'Executive Dashboard',
                content: `
The **Executive Dashboard** (Reports → Executive Dashboard) is the primary national-level analytics view. It contains 10 sections navigable from a sticky tab bar:

| Section | What it shows |
|---|---|
| Overview | KPI cards: avg score, facilities assessed, high performers, needs attention, overdue schedules |
| Trend | 12-month national compliance trend with 90% target and 85% benchmark lines |
| Schedules | Audit schedule status: completed, in progress, overdue, upcoming |
| Clauses | Radar chart, clause bar chart, and clause score heatmap by facility |
| Sub-Standards | Per-clause average score cards with progress bars |
| Low MEs | Clause results below 70% flagged for attention |
| KPI vs Actuals | Gauge charts showing performance against defined targets |
| Quality Index | Composite QoC + survey weighted index per facility |
| Survey Dashboard | Inpatient/outpatient satisfaction survey results |
| Period Comparison | Multi-cycle comparison (up to 5 periods) |

**Main filter bar** (sticky at top): Facility, level, clause, date range, and period filters — set once and all sections update.
                `
            },
            {
                id: 'db-period',
                title: 'Period Comparison',
                content: `
The **Period Comparison** section (Section 10 of the Executive Dashboard) allows you to compare up to 5 audit periods side by side.

**How to use:**
1. Scroll to Period Comparison (or click "🔄 Period Comparison" in the tab bar)
2. Use the Cycle 1–5 selectors to pick which periods to compare
3. Facility, level, and clause filters from the main bar automatically apply

**What it shows:**
- Overall score cards per period + a Δ change card (last vs first cycle)
- Clause grouped bar chart — all 10 clauses, one bar per period
- Clause trend lines — trajectory of each clause across cycles
- Clause table — scores, stars, and delta per period
- Facility delta chart — which facilities improved or regressed vs baseline
- Facility table — all facility scores across selected periods

**Note:** Requires at least 2 active periods configured in Settings → Assessment Periods.
                `
            },
            {
                id: 'db-filters',
                title: 'Using Dashboard Filters',
                content: `
The sticky filter bar at the top of the Executive Dashboard has 5 filters:

| Filter | Effect |
|---|---|
| Facility | Show data for one specific facility only |
| Level | Filter by facility type (e.g. Clinic without Maternity) |
| Clause | Filter all charts to one clause |
| Date Range | Manual date window override |
| Assessment Period | Snap to a defined period's exact date range |

Selecting an Assessment Period automatically adjusts the date range to match that period's start and end dates.

Click **Clear filters ×** to reset all back to national view.

The Period Comparison section inherits facility, level, and clause filters but uses its own cycle selectors for the date dimension.
                `
            }
        ]
    },
    {
        id: 'reports',
        icon: '📄',
        title: 'Reports & Exports',
        color: '#eb2f96',
        topics: [
            {
                id: 'rp-facility',
                title: 'Facility Audit Report',
                content: `
The **Facility Report** (Reports → Facility Report) generates a comprehensive audit report for a selected facility.

**To generate:**
1. Select the facility
2. Select the assessment period from the dropdown — e.g. *"FY2025/26 — 81.3% · 15 Jul 25 (10 clauses)"*
3. The report loads automatically in the preview panel

**Export options:**
| Format | Best for |
|---|---|
| Print / Save PDF | Quick sharing — fast, browser-based |
| Narrative PDF Report | Formal submission — pixel-perfect with full narrative text per clause |
| Download Excel | Data analysis — one sheet per clause |

**Narrative PDF structure:**
- Cover page with facility details, overall score, and star rating
- Executive summary with narrative paragraph
- Table of contents and abbreviations
- Introduction, scope, objectives, and methodology
- Findings per clause: summary table + nonconformity table
- Corrective Action Plan (if CAP items exist)
- Conclusion with improvement recommendations

**Note:** The dropdown groups assessments by period label. Select the entry showing the clause count (e.g. *10 clauses*) for the full report rather than a single-clause entry.
                `
            },
            {
                id: 'rp-excel',
                title: 'Excel Export',
                content: `
The Excel export produces a workbook with:
- **Cover Page** sheet: facility info, overall score, and section scores table
- **One sheet per clause** (AACC through ISM) with all ME responses, fulfillment status, comments, observations, and criticality

Each clause sheet has:
- Colour-coded fulfillment cells (green = Met, orange = Partial, red = Not Met)
- Auto-filter on the header row
- Frozen panes to keep the header visible while scrolling
- KCS (Key Critical Standard) MEs highlighted in red

The file is named: \`QoC_Assessment_{FacilityName}_{Date}.xlsx\`
                `
            },
            {
                id: 'rp-card-export',
                title: 'Per-Card PNG / PDF Export',
                content: `
Every chart card in the Executive Dashboard has **PNG** and **PDF** export buttons in its top-right corner.

- **PNG** — Captures the card at 2.5× scale as a high-resolution image
- **PDF** — Captures at 2.0× scale on A4 landscape with a dark header stripe

Export buttons are hidden during capture so they don't appear in the output.

The **Download PDF** button in the dashboard header captures all 10 sections into a single multi-page A4 landscape PDF.
                `
            }
        ]
    },
    {
        id: 'surveys',
        icon: '📝',
        title: 'Customer Surveys',
        color: '#13c2c2',
        topics: [
            {
                id: 'sv-public',
                title: 'Public Survey Link',
                content: `
Patients can submit satisfaction surveys without logging in using the public survey URL:

\`https://your-domain/qoc/survey/inpatient\`
\`https://your-domain/qoc/survey/outpatient\`

**Inpatient survey** — For patients admitted to a ward. Collects: facility, region, ward admitted, arrival/departure time, ratings on staff attitude, supplies, cleanliness, food, communication, and overall satisfaction.

**Outpatient survey** — For patients seen in outpatient departments. Collects: facility, region, departments visited, timing, and quality ratings.

Surveys are automatically linked to the current active period and appear in the Executive Dashboard's Survey section.
                `
            },
            {
                id: 'sv-manage',
                title: 'Managing Survey Data',
                content: `
Go to **National Oversight → Customer Surveys** to:
- View all submitted surveys in a table
- Filter by facility, period, survey type, and date
- View overall scores and satisfaction ratings
- Export survey data

**Scoring:** Each survey item is scored 0–100. The overall score is the mean of all scored items.

**Satisfaction threshold:** A survey scoring ≥ 70% is considered "satisfied." The Executive Dashboard shows the percentage of surveys meeting this threshold.

**Quality Index integration:** Survey scores are weighted with QoC audit scores (default 20% survey / 80% QoC) to produce the Quality Index shown in National Oversight → Quality Index.
                `
            }
        ]
    },
    {
        id: 'settings',
        icon: '⚙️',
        title: 'Settings',
        color: '#f59e0b',
        topics: [
            {
                id: 'st-periods',
                title: 'Assessment Periods',
                content: `
Periods define the audit cycle window (e.g. FY2025/26: 1 April 2025 – 31 March 2026).

Go to **Settings → Assessment Periods** to:
1. Create a new period with a label, start date, and end date
2. Mark one period as **Current** — this becomes the default in all dashboards and the public survey form
3. Mark periods as **Active** to include them in filter dropdowns

**Best practice:** Always set exactly one period as Current. Dashboards automatically default to the current period's date range on first load.

**Period Comparison:** Once you have 2+ active periods with assessment data, the Executive Dashboard's Period Comparison section (Section 10) can compare performance across them.
                `
            },
            {
                id: 'st-schedules',
                title: 'Audit Schedules',
                content: `
Audit Schedules define when each facility is planned to be audited within a period.

Go to **Settings → Audit Schedules** to:
1. Create a new schedule by selecting facility, period, and planned date
2. Assign a lead assessor from the user list
3. Track status: Planned → In Progress → Completed / Overdue

**How schedules link to assessments:**
When an assessor creates an assessment, they can link it to an existing schedule. Once an assessment is submitted, the schedule status automatically updates to "Completed."

Overdue schedules (planned date passed, no assessment) appear in the Executive Dashboard's Schedules section and the Overview section's KPI cards.

**Bulk schedule creation:**
Use the bulk create feature to generate multiple schedules at once — select multiple facilities and a date range to auto-generate one schedule per facility.
                `
            },
            {
                id: 'st-access',
                title: 'Access Control',
                content: `
**Settings → Access Control** is the central place to manage what each role can see and do in the platform.

It has two tabs:

**Role Permissions tab:**
- Shows all roles as cards, each with a badge showing how many modules are currently enabled
- Click any role to open the permissions modal
- The modal shows every active module grouped by category (Administration, Assets & Stock, Tickets & Jobs, Quality of Care)
- Toggle each module on or off with a switch
- **Enable All** / **Disable All** buttons for quick bulk changes
- Click **Save Permissions** — changes take effect on users' next login

**Important:** Permission changes do not affect currently logged-in sessions. Users must log out and back in for new permissions to apply.

**Module Management tab:**
- Shows the complete \`modules\` table — all available system modules
- Create new modules with **New Module**:
  - **Key** — unique lowercase identifier (e.g. \`qoc_reports\`) — cannot be changed after creation
  - **Display Name** — human-readable label shown in the permissions grid
  - **Group** — which category it appears under in the permissions modal
  - **Applies To** — CLIENT (facility users only), ADMIN, or BOTH
  - **National Level Only** — restricts the module to national-level users
  - **Active** — inactive modules are hidden from the permissions grid but not deleted
- Edit any module's name, group, or visibility
- Delete custom modules (built-in modules with id ≤ 221 are protected)

**How permissions flow:**
1. Module added to \`modules\` table with a unique \`key\`
2. Role is given that module via the Permissions modal → saved to \`roles.app_modules\` as CSV
3. On login, the server sends the user's enabled modules as \`{ module_key: 1 }\`
4. Frontend reads this from localStorage via \`util.getModules()\`
5. Nav items check \`modules['module_key'] === 1\` to show/hide
6. Backend \`checkAccess('module_key')\` enforces it on every API call
                `
            },
            {
                id: 'st-hie',
                title: 'HIE Facility Sync',
                content: `
**Settings → HIE Facility Sync** connects the platform to the national Health Information Exchange (HIE) to sync the master facility list using the HL7 FHIR R4 standard.

**Why use HIE sync?**
The Ministry of Health's HIE is the authoritative master facility registry. Syncing from HIE ensures your QoC facility list stays aligned with the national record — including new facilities, name changes, and updated region/type data.

**Setup (one-time):**

1. Enter the **FHIR Base URL** — e.g. \`https://hie.health.gov.sz/fhir\`
2. Select **Auth Type** — Bearer Token, API Key, or None
3. Paste the **Auth Token** if required
4. Choose **Sync Mode**:
   - *Add new + Update changed* — adds new facilities AND updates existing ones when HIE data has changed
   - *Add new only* — only adds facilities that don't yet exist in QoC (never overwrites local data)
5. Click **Save Configuration**
6. Click **Test Connection** — should show green "Connected" with the FHIR server version

**Running a sync:**

1. Click **Fetch from HIE & Preview** — downloads all \`Organization\` resources from HIE and shows a diff table:
   - 🟢 **New** — facility exists in HIE but not in QoC
   - 🔵 **Update** — facility exists in both but fields have changed
   - **No Change** — already up to date
   - ⚠ **Blank** — HIE didn't populate that field (common for region or type)
2. Review the table — check the Action and Changes columns before proceeding
3. Click **Sync Now** → confirm in the modal → sync applies

**Blank fields:**
If the HIE record doesn't have region or facility type, the facility is still imported (with name and HIE ID). Blank fields are counted in the result message — edit them manually in Administration → Facilities after sync.

**FHIR field mapping:**
| FHIR field | QoC field |
|---|---|
| Organization.id | hie_id (internal link) |
| Organization.name | Facility name |
| Organization.address[0].district | Region |
| Organization.type[0].coding[0].display | Site type / Level |
| Organization.telecom[phone].value | Phone |

**Login badge:**
If HIE sync has been configured, a background check runs on login. If there are new or changed facilities in HIE, a red badge appears on the HIE Facility Sync menu item showing the pending count. The badge is cached for 6 hours — it won't re-check on every page navigation.

**Facilities are never deleted by sync.** Only additions and updates are applied.

**Sync History:**
The Sync History table at the bottom of the page shows every sync run with counts of new, updated, skipped, and blank-field records.
                `
            },
            {
                id: 'st-kpis',
                title: 'KPI Targets',
                content: `
KPI Targets are configured from **National Oversight → KPI Targets** and define expected performance levels by clause and period.

**To add targets:**
1. Go to National Oversight → KPI Targets
2. Click **Add Target**
3. Select the period, clause, and facility level
4. Set the target percentage (e.g. 85% for IPCM in FY2025/26)
5. Save

The Executive Dashboard's **KPI vs Actuals** section (Section 7) compares actual clause scores against these targets using gauge charts. A target line is shown alongside the actual score.

If no KPI targets are defined, the KPI section shows an informational message prompting you to add targets.
                `
            }
        ]
    },
    {
        id: 'standards-setup',
        icon: '📐',
        title: 'Standards Setup',
        color: '#1890ff',
        topics: [
            {
                id: 'st-clauses',
                title: 'The 10 QoC Clauses',
                content: `
The QoC standard is divided into 10 clauses, each covering a different aspect of health service delivery:

| # | Code | Title |
|---|---|---|
| 1 | AACC | Access, Assessment and Continuity of Care |
| 2 | COP | Care of the Patient |
| 3 | MOM | Management of Medication |
| 4 | PRPE | Patient Rights and Patient Education |
| 5 | IPCM | Infection Prevention and Control Management |
| 6 | PSQI | Patient Safety and Quality Improvement |
| 7 | GROM | Governance, Roles and Responsibilities of Management |
| 8 | FMS | Facility Management, Safety and Support Services |
| 9 | HRE | Human Resource and Education |
| 10 | ISM | Information System Management |

Each clause contains multiple **Standards**, divided into **Sub-Standards** and **Measurable Elements (MEs)**.
                `
            },
            {
                id: 'st-rating',
                title: 'Rating Scale',
                content: `
QoC scores map to a 5-star rating scale aligned with ISO accreditation readiness:

| Score Range | Rating | Stars | ISO Status |
|---|---|---|---|
| 98 – 100% | Outstanding | ★★★★★ | Ready for ISO accreditation |
| 95 – 97.9% | Excellent | ★★★★ | Ready for ISO accreditation |
| 90 – 94.9% | Good | ★★★ | Almost ready for ISO accreditation |
| 85 – 89.9% | Fair | ★★ | Somewhat ready for ISO accreditation |
| 70 – 84.9% | Requires Improvement | ★ | Not ready for ISO accreditation |
| 0 – 69.9% | Inadequate | — | Cause for concern |

The **Overall Facility Score** is the mean of all assessed clause scores for a given period.
                `
            },
            {
                id: 'st-admin',
                title: 'Managing Standards (Admin)',
                content: `
Users with \`qoc_admin\` can access **Standards Setup** to:
- View all clauses, standards, sub-standards, and measurable elements
- Mark specific MEs as Key Critical Standards (KCS)
- Update ME text or add new MEs
- Manage the standards hierarchy

**Warning:** Changes to MEs affect all future assessments. Existing responses are not retroactively changed. Consult the system administrator before modifying active MEs.
                `
            }
        ]
    },
    {
        id: 'administration',
        icon: '👤',
        title: 'Administration',
        color: '#60a5fa',
        topics: [
            {
                id: 'adm-roles',
                title: 'Roles & Users',
                content: `
**Administration → Roles & Users** manages user accounts and role definitions. Requires \`manage_roles_users\` permission.

**Managing users:**
1. Go to **Roles & Users** → **Users** tab
2. Click **Add User** to create a new account
3. Set username, email, and password
4. Assign a **Role** — the role controls which modules the user can access

**Managing roles:**
1. Go to **Roles & Users** → **Roles** tab
2. Click **Add Role** to create a new role (e.g. "Assessor", "Approver", "National Analyst")
3. Check the permission modules to enable for this role

**Note:** For fine-grained control over which modules each role can see — including new custom modules — use **Settings → Access Control** rather than editing roles here.

**Best practice:** Create separate roles per function. Don't give everyone \`qoc_admin\`. Typical role structure:
- **Facility Assessor** → \`qoc_assess\`
- **Approval Officer** → \`qoc_approve\`
- **National Analyst** → \`qoc_view\`
- **QoC Administrator** → \`qoc_admin\`
- **Facility Manager** → \`qoc_view\` + \`manage_facilities\`
                `
            },
            {
                id: 'adm-facilities',
                title: 'Facilities',
                content: `
**Administration → Facilities** manages the master list of health facilities. Requires \`manage_facilities\` permission.

**Facility record fields:**
| Field | Description |
|---|---|
| Business Name | Full official facility name |
| Site Type / Level | e.g. Clinic without Maternity, Regional Hospital |
| Located Region | Eswatini region (Hhohho, Manzini, Lubombo, Shiselweni) |
| Facility Code | Unique code |
| Status | Active / Inactive |

**Adding a facility manually:**
1. Click **Add Facility**
2. Fill in Business Name, Site Type, and Region (mandatory)
3. Set status to Active and save

**Adding facilities from HIE:**
Use **Settings → HIE Facility Sync** to import facilities directly from the national Health Information Exchange. This is the recommended method for ensuring alignment with the national registry. See the HIE Sync section for full instructions.

**Why this matters:** Facility name, region, and level appear in all exports, dashboards, and dashboard filters. Ensure the Site Type is set correctly — the "Level" dashboard filter uses this field.
                `
            },
            {
                id: 'adm-profile',
                title: 'My Profile',
                content: `
Access your profile from the **Profile** link in the navigation header.

**Available actions:**
- Update your display name and contact details
- Change your password (requires current password)
- View your assigned role and permissions

**Changing your password:**
1. Enter your current password
2. Enter and confirm the new password
3. Click Save

If you forget your password, contact your system administrator. There is no self-service password reset.
                `
            }
        ]
    },
    {
        id: 'troubleshooting',
        icon: '🔧',
        title: 'Troubleshooting',
        color: '#f5222d',
        topics: [
            {
                id: 'tr-dashboard',
                title: 'Dashboard Shows No Data',
                content: `
**Problem:** Charts and KPI cards show 0 or "No data."

**Check these:**
1. **Period filter** — The Executive Dashboard defaults to the current period. If no assessments exist within that period's date range, nothing will show. Try clearing the period filter or selecting "All."
2. **Assessment status** — Only "Submitted" or "Approved" assessments appear in dashboards. Drafts are excluded.
3. **Date range** — The date range must overlap with your assessment dates.
4. **Scores not calculated** — If assessments were bulk-imported without recalculating scores, the overall_percentage field may be 0. Run the backfill SQL provided by your administrator or ask them to recalculate scores.
                `
            },
            {
                id: 'tr-report',
                title: 'Facility Report Shows "Not Assessed"',
                content: `
**Problem:** The Facility Report shows data for only one clause; others show "Not assessed."

**Cause:** The report was opened from a single-clause assessment entry rather than the grouped period entry.

**Fix:**
1. Open the Facility Report modal
2. In the assessment dropdown, look for an entry showing "(10 clauses)" — e.g. *"FY2025/26 — 81.3% · 15 Jul 25 (10 clauses)"*
3. Select that grouped entry — it loads all 10 clauses together

If the dropdown only shows individual clause entries, it means assessments have different period labels. Ensure all assessments for the same facility in the same audit cycle share the same period label.
                `
            },
            {
                id: 'tr-import',
                title: 'KoboCollect Import Errors',
                content: `
**"me_id = 0" error or missing responses after import:**
KoboCollect exports use short ME codes (e.g. \`1.1\`) but the database uses prefixed codes (e.g. \`AACC.1.1\`). Select the correct clause in the import dropdown before importing — this tells the system which prefix to apply.

**Duplicate entry error:**
The same assessment has already been imported. Check the assessments list before re-importing.

**Wrong scores after import:**
Scores are recalculated automatically after import. If scores still look wrong, verify that all MEs in the import file have valid fulfillment values (Met, Partially Met, Not Met, Not Applicable, or Not Checked).
                `
            },
            {
                id: 'tr-permissions',
                title: 'User Cannot See a Page',
                content: `
**Problem:** A user reports they cannot see a navigation item or gets "Access denied."

**Steps to resolve:**
1. Go to **Settings → Access Control**
2. Click the user's role card in the **Role Permissions** tab
3. Check whether the relevant module is enabled (toggled on) for that role
4. If not, enable it and click **Save Permissions**
5. Ask the user to **log out and log back in** — permission changes only apply on fresh login

**If the module doesn't appear in the grid:**
The module may be inactive. Go to the **Module Management** tab, find the module, and set its status to Active.

**If the module doesn't exist:**
Create it via **Module Management → New Module**. After creating it, go back to Role Permissions and enable it for the appropriate roles.
                `
            },
            {
                id: 'tr-hie',
                title: 'HIE Sync Issues',
                content: `
**"Connection failed" on Test Connection:**
- Verify the FHIR Base URL is correct and reachable from the server (not just from your browser)
- Confirm the auth token is valid and has not expired
- Check that the server's firewall allows outbound HTTP/HTTPS requests to the HIE

**Facilities not appearing after sync:**
- Check the Sync History table — confirm the sync ran and check the "New" count
- Verify Sync Mode is set to "Add new + Update changed" (not "Add new only" if you expected updates)
- Open the Preview table and confirm the facilities show as "New" before syncing

**Blank fields after sync:**
This is expected if the HIE doesn't populate region or type for those facilities. Edit them manually in Administration → Facilities. On the next sync, if HIE has been updated with the missing data, it will be filled automatically (if Sync Mode includes updates).

**Badge not clearing after sync:**
The login badge is cached in localStorage for 6 hours. After a successful sync, the badge clears immediately. If it persists, hard-refresh the page (Ctrl+Shift+R).
                `
            },
            {
                id: 'tr-survey',
                title: 'Public Survey 500 Error',
                content: `
**Error:** Survey submission returns a database error about a missing column.

**Fix:** Run the following SQL in phpMyAdmin:
\`\`\`sql
ALTER TABLE \`qoc_customer_surveys\`
    ADD COLUMN \`submitted_via\` VARCHAR(50) NULL DEFAULT NULL
    AFTER \`overall_satisfaction\`;
\`\`\`

This adds the column that records how the survey was submitted (public_link vs staff_entry).
                `
            }
        ]
    },
];

// ─── Render markdown-ish content ──────────────────────────────────────────────
function renderContent(text) {
    const lines = text.trim().split('\n');
    const elements = [];
    let i = 0;
    let tableRows = [];
    let inTable = false;

    const flushTable = () => {
        if (tableRows.length < 2) { tableRows = []; inTable = false; return; }
        const headers = tableRows[0].split('|').map(h => h.trim()).filter(Boolean);
        const rows = tableRows.slice(2).map(r => r.split('|').map(c => c.trim()).filter(Boolean));
        elements.push(
            <div key={`table-${i}`} style={{ overflowX: 'auto', marginBottom: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                        <tr style={{ background: '#f0f5ff' }}>
                            {headers.map((h, hi) => (
                                <th key={hi} style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #d6e4ff', fontWeight: 700, color: '#1a1a2e' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, ri) => (
                            <tr key={ri} style={{ background: ri % 2 ? '#fafbff' : '#fff' }}>
                                {row.map((cell, ci) => (
                                    <td key={ci} style={{ padding: '7px 12px', borderBottom: '1px solid #e8f0fe', fontSize: 13 }}>
                                        {renderInline(cell)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
        tableRows = []; inTable = false;
    };

    while (i < lines.length) {
        const line = lines[i];
        if (line.startsWith('|')) { inTable = true; tableRows.push(line); i++; continue; }
        if (inTable) flushTable();

        if (line.startsWith('**') && line.endsWith('**') && !line.slice(2, -2).includes('**')) {
            elements.push(<div key={i} style={{ fontWeight: 800, fontSize: 14, color: '#0f172a', marginTop: 16, marginBottom: 6 }}>{line.slice(2, -2)}</div>);
        } else if (line.startsWith('```')) {
            const codeLines = [];
            i++;
            while (i < lines.length && !lines[i].startsWith('```')) { codeLines.push(lines[i]); i++; }
            elements.push(
                <pre key={i} style={{ background: '#0f172a', color: '#e2e8f0', padding: '14px 18px', borderRadius: 8, fontSize: 12, overflowX: 'auto', marginBottom: 16, lineHeight: 1.7 }}>
                    <code>{codeLines.join('\n')}</code>
                </pre>
            );
        } else if (line.startsWith('- ')) {
            const items = [line.slice(2)];
            i++;
            while (i < lines.length && lines[i].startsWith('- ')) { items.push(lines[i].slice(2)); i++; }
            elements.push(
                <ul key={i} style={{ paddingLeft: 20, marginBottom: 12 }}>
                    {items.map((item, ii) => <li key={ii} style={{ marginBottom: 4, fontSize: 13, color: '#334155', lineHeight: 1.7 }}>{renderInline(item)}</li>)}
                </ul>
            );
            continue;
        } else if (line.match(/^\d+\. /)) {
            const items = [line.replace(/^\d+\. /, '')];
            i++;
            while (i < lines.length && lines[i].match(/^\d+\. /)) { items.push(lines[i].replace(/^\d+\. /, '')); i++; }
            elements.push(
                <ol key={i} style={{ paddingLeft: 20, marginBottom: 12 }}>
                    {items.map((item, ii) => <li key={ii} style={{ marginBottom: 4, fontSize: 13, color: '#334155', lineHeight: 1.7 }}>{renderInline(item)}</li>)}
                </ol>
            );
            continue;
        } else if (line === '') {
            elements.push(<div key={i} style={{ height: 8 }} />);
        } else {
            elements.push(<p key={i} style={{ fontSize: 13, color: '#334155', lineHeight: 1.8, marginBottom: 10 }}>{renderInline(line)}</p>);
        }
        i++;
    }
    if (inTable) flushTable();
    return elements;
}

function renderInline(text) {
    const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) return <strong key={i} style={{ color: '#0f172a' }}>{part.slice(2, -2)}</strong>;
        if (part.startsWith('`') && part.endsWith('`')) return <code key={i} style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 3, fontSize: 12, fontFamily: 'monospace', color: '#0369a1' }}>{part.slice(1, -1)}</code>;
        return part;
    });
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function QoCUserManual() {
    const [activeSection, setActiveSection] = useState('getting-started');
    const [activeTopic,   setActiveTopic]   = useState('gs-overview');
    const [search,        setSearch]        = useState('');
    const [sidebarOpen,   setSidebarOpen]   = useState(true);
    const contentRef = useRef(null);

    const allTopics = SECTIONS.flatMap(s => s.topics.map(t => ({ ...t, sectionId: s.id, sectionTitle: s.title, sectionColor: s.color, sectionIcon: s.icon })));

    const searchResults = search.trim().length > 1
        ? allTopics.filter(t => t.title.toLowerCase().includes(search.toLowerCase()) || t.content.toLowerCase().includes(search.toLowerCase()))
        : [];

    const currentSection = SECTIONS.find(s => s.id === activeSection);
    const currentTopic   = allTopics.find(t => t.id === activeTopic);

    const navigate = (sectionId, topicId) => {
        setActiveSection(sectionId);
        setActiveTopic(topicId);
        setSearch('');
        if (contentRef.current) contentRef.current.scrollTop = 0;
    };

    return (
        <div className="page-content" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div className="page-head uc d-flex" style={{ background: 'linear-gradient(135deg,#0f172a,#1e3a5f)', padding: '14px 24px', flexShrink: 0 }}>
                <div className="my-auto" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <BookOutlined style={{ fontSize: 22, color: '#60a5fa' }} />
                    <div>
                        <h2 style={{ margin: 0, color: '#fff', fontSize: 18, fontWeight: 800 }}>User Manual</h2>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>Eswatini QoC Platform — Complete Guide</div>
                    </div>
                </div>
                <div className="my-auto ml-auto" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Input
                        prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                        placeholder="Search manual…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ width: 240, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff' }}
                        allowClear
                    />
                    <span onClick={() => setSidebarOpen(v => !v)} style={{ cursor: 'pointer', color: 'rgba(255,255,255,0.6)', fontSize: 18, padding: '4px 8px' }}>
                        {sidebarOpen ? <CloseOutlined /> : <MenuOutlined />}
                    </span>
                </div>
            </div>

            {/* Search overlay */}
            {search.trim().length > 1 && (
                <div style={{ position: 'absolute', top: 64, right: 24, zIndex: 999, width: 340, background: '#fff', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', border: '1px solid #e8f0fe', maxHeight: 400, overflowY: 'auto' }}>
                    <div style={{ padding: '10px 16px', borderBottom: '1px solid #f0f0f0', fontSize: 12, color: '#888' }}>
                        {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{search}"
                    </div>
                    {searchResults.length === 0
                        ? <div style={{ padding: 20, textAlign: 'center', color: '#aaa', fontSize: 13 }}>No results found</div>
                        : searchResults.map(t => (
                            <div key={t.id} onClick={() => navigate(t.sectionId, t.id)}
                                style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f8faff'}
                                onMouseLeave={e => e.currentTarget.style.background = ''}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{t.title}</div>
                                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                                    <span style={{ color: t.sectionColor }}>{t.sectionIcon} {t.sectionTitle}</span>
                                </div>
                            </div>
                        ))
                    }
                </div>
            )}

            {/* Body */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden', background: '#f8faff' }}>
                {/* Sidebar */}
                {sidebarOpen && (
                    <div style={{ width: 260, flexShrink: 0, background: '#fff', borderRight: '1px solid #e8f0fe', overflowY: 'auto', padding: '16px 0' }}>
                        {SECTIONS.map(section => (
                            <div key={section.id} style={{ marginBottom: 4 }}>
                                <div onClick={() => navigate(section.id, section.topics[0].id)}
                                    style={{
                                        padding: '8px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                                        background: activeSection === section.id ? `${section.color}12` : 'transparent',
                                        borderLeft: activeSection === section.id ? `3px solid ${section.color}` : '3px solid transparent',
                                        transition: 'all 0.15s',
                                    }}
                                    onMouseEnter={e => { if (activeSection !== section.id) e.currentTarget.style.background = '#f8faff'; }}
                                    onMouseLeave={e => { if (activeSection !== section.id) e.currentTarget.style.background = 'transparent'; }}>
                                    <span style={{ fontSize: 15 }}>{section.icon}</span>
                                    <span style={{ fontSize: 13, fontWeight: activeSection === section.id ? 700 : 500, color: activeSection === section.id ? section.color : '#475569' }}>
                                        {section.title}
                                    </span>
                                </div>
                                {activeSection === section.id && (
                                    <div style={{ paddingLeft: 8, marginTop: 2 }}>
                                        {section.topics.map(topic => (
                                            <div key={topic.id}
                                                onClick={() => { setActiveTopic(topic.id); if (contentRef.current) contentRef.current.scrollTop = 0; }}
                                                style={{
                                                    padding: '6px 20px 6px 28px', cursor: 'pointer', fontSize: 12,
                                                    color: activeTopic === topic.id ? section.color : '#64748b',
                                                    fontWeight: activeTopic === topic.id ? 700 : 400,
                                                    background: activeTopic === topic.id ? `${section.color}10` : 'transparent',
                                                    borderRadius: 4,
                                                    borderLeft: activeTopic === topic.id ? `2px solid ${section.color}` : '2px solid transparent',
                                                    transition: 'all 0.1s', display: 'flex', alignItems: 'center', gap: 6,
                                                }}
                                                onMouseEnter={e => { if (activeTopic !== topic.id) e.currentTarget.style.background = '#f1f5f9'; }}
                                                onMouseLeave={e => { if (activeTopic !== topic.id) e.currentTarget.style.background = 'transparent'; }}>
                                                <span style={{ width: 5, height: 5, borderRadius: '50%', flexShrink: 0, background: activeTopic === topic.id ? section.color : '#cbd5e1' }} />
                                                {topic.title}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                        <div style={{ margin: '24px 20px 8px', padding: '12px', background: '#f8faff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 4 }}>QoC Platform</div>
                            <div style={{ fontSize: 11, color: '#cbd5e1' }}>Eswatini Ministry of Health</div>
                            <div style={{ fontSize: 11, color: '#cbd5e1' }}>National Quality Management Programme</div>
                        </div>
                    </div>
                )}

                {/* Content */}
                <div ref={contentRef} style={{ flex: 1, overflowY: 'auto', padding: '32px 40px' }}>
                    {currentTopic && currentSection && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20, fontSize: 12, color: '#94a3b8' }}>
                                <span style={{ color: currentSection.color }}>{currentSection.icon} {currentSection.title}</span>
                                <span>›</span>
                                <span style={{ color: '#475569' }}>{currentTopic.title}</span>
                            </div>
                            <div style={{ marginBottom: 28, paddingBottom: 20, borderBottom: `2px solid ${currentSection.color}20` }}>
                                <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: '#0f172a', lineHeight: 1.3 }}>{currentTopic.title}</h1>
                                <Tag style={{ marginTop: 10, background: `${currentSection.color}15`, color: currentSection.color, border: 'none', fontWeight: 600 }}>
                                    {currentSection.icon} {currentSection.title}
                                </Tag>
                            </div>
                            <div style={{ maxWidth: 780 }}>
                                {renderContent(currentTopic.content)}
                            </div>
                            {/* Prev / Next navigation */}
                            <div style={{ marginTop: 48, paddingTop: 20, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                                {(() => {
                                    const flat = SECTIONS.flatMap(s => s.topics.map(t => ({ ...t, sectionId: s.id, sectionColor: s.color })));
                                    const idx  = flat.findIndex(t => t.id === activeTopic);
                                    const prev = flat[idx - 1];
                                    const next = flat[idx + 1];
                                    return (
                                        <>
                                            {prev
                                                ? <div onClick={() => navigate(prev.sectionId, prev.id)}
                                                    style={{ cursor: 'pointer', padding: '10px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', maxWidth: '45%' }}
                                                    onMouseEnter={e => e.currentTarget.style.borderColor = prev.sectionColor}
                                                    onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}>
                                                    <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>← Previous</div>
                                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{prev.title}</div>
                                                </div>
                                                : <div />
                                            }
                                            {next
                                                ? <div onClick={() => navigate(next.sectionId, next.id)}
                                                    style={{ cursor: 'pointer', padding: '10px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', maxWidth: '45%', textAlign: 'right' }}
                                                    onMouseEnter={e => e.currentTarget.style.borderColor = next.sectionColor}
                                                    onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}>
                                                    <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>Next →</div>
                                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{next.title}</div>
                                                </div>
                                                : <div />
                                            }
                                        </>
                                    );
                                })()}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}